"use client";

import { useId, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import { analyzeUserComment, USER_COMMENT_SOURCE_LIMIT } from "./userComment";
import { GameUserComment } from "./GameUserComment";
import { X } from "lucide-react";
import type { CommentSaveResult } from "./useGameAnalysisCollection";
import { uncertainFlagMessage } from "./flagFeedback";

type GameUserCommentEditorProps = {
  title: string;
  initialSource: string | null;
  pending: boolean;
  recovering: boolean;
  readFailed: boolean;
  onRefresh: () => void;
  onSave: (value: string | null, onResult: (result: CommentSaveResult) => void) => void;
  onDismiss: () => void;
  onReturnFocus: () => void;
};

export function GameUserCommentEditor({ title, initialSource, pending, recovering, readFailed, onRefresh, onSave, onDismiss, onReturnFocus }: GameUserCommentEditorProps) {
  const [source, setSource] = useState(initialSource ?? "");
  const currentSource = useRef(source);
  currentSource.current = source;
  const [saveError, setSaveError] = useState<string>();
  const id = useId();
  const errorId = useId();
  const field = useRef<HTMLTextAreaElement>(null);
  const analysis = analyzeUserComment(source);
  const submit = () => {
    if (pending || recovering || !analysis.valid) return;
    setSaveError(undefined);
    const submittedSource = source;
    onSave(analysis.userComment, (result) => {
      if (result.status === "rejected") { setSaveError(result.message); return; }
      if (result.status === "recovered") {
        if (!result.matches) {
          setSaveError("Отправленная версия не подтверждена. Проверьте текст и сохраните снова.");
          return;
        }
        if (currentSource.current !== submittedSource) {
          setSaveError("Отправленная версия сохранена. Текущий текст ещё не сохранён.");
          return;
        }
      }
      onDismiss();
    });
  };
  return <Dialog open onOpenChange={(open) => { if (!open && !pending) onDismiss(); }}>
    <DialogContent data-user-comment-editor showCloseButton={false} className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-3xl overflow-y-auto" onEscapeKeyDown={(event) => { if (pending) event.preventDefault(); }} onPointerDownOutside={(event) => event.preventDefault()} onOpenAutoFocus={(event) => { event.preventDefault(); field.current?.focus(); }} onCloseAutoFocus={(event) => { event.preventDefault(); onReturnFocus(); }}>
      <Button type="button" variant="ghost" size="icon" className="absolute right-2 top-2 h-11 w-11" aria-label="Закрыть" disabled={pending} onFocus={(event) => event.currentTarget.scrollIntoView({ block: "nearest" })} onClick={onDismiss}><X /></Button>
      <DialogTitle className="pr-10">Комментарий пользователя</DialogTitle>
      <DialogDescription>{title}</DialogDescription>
      <label htmlFor={id}>Исходный текст комментария</label>
      <div className="space-y-1">
        <Textarea ref={field} id={id} value={source} disabled={pending} aria-invalid={!analysis.valid} aria-describedby={!analysis.valid ? errorId : undefined} onChange={(event) => setSource(event.target.value)} />
        <p aria-label="Длина комментария" className="text-xs text-muted-foreground">{analysis.length} / {USER_COMMENT_SOURCE_LIMIT}</p>
      </div>
      {!analysis.valid && <p role="alert" id={errorId} className="text-sm text-destructive">Не более {USER_COMMENT_SOURCE_LIMIT} символов с учётом разметки</p>}
      <div className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
        <p>Форматирование: теги выделяют текст; Enter или <code>{"<br>"}</code> добавляют перенос строки. Адреса HTTP(S) и www становятся ссылками.</p>
        <p><code>{"<b>жирный</b>"}</code>{" · "}<code>{"<i>курсив</i>"}</code>{" · "}<code>{"<u>подчёркнутый</u>"}</code>{" · "}<code>{"<s>зачёркнутый</s>"}</code></p>
        <p><code>{'<span style="color:red;font-size:24px">текст</span>'}</code>{" · "}<code>{'<font color="blue" size="5">текст</font>'}</code></p>
        <p><code>{'<a href="https://example.com">ссылка</a>'}</code></p>
      </div>
      <div className="grid min-w-0 gap-1">
        <h3 className="font-medium">Предпросмотр</h3>
        {analysis.valid && analysis.userComment !== null && <GameUserComment source={analysis.userComment} label="Предпросмотр" />}
        {analysis.valid && analysis.userComment === null && <div role="region" aria-label="Предпросмотр">Комментарий отсутствует</div>}
        {!analysis.valid && <div role="region" aria-label="Предпросмотр">Предпросмотр недоступен: превышен лимит</div>}
      </div>
      {saveError && <p role="alert" className="text-sm text-destructive">{saveError}</p>}
      {(recovering || readFailed) && <div>
        <p role="alert" className="text-sm text-destructive">{readFailed ? "Не удалось загрузить партии" : uncertainFlagMessage}</p>
        <Button type="button" variant="outline" disabled={pending} onClick={onRefresh}>Обновить список</Button>
      </div>}
      <DialogFooter>
        <Button type="button" variant="outline" disabled={pending} onClick={onDismiss}>Отмена</Button>
        <Button type="button" disabled={pending || recovering || !analysis.valid} onClick={submit}>{pending ? <span role="status">Сохранение…</span> : "Сохранить"}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
